import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useProducts } from '@/contexts/ProductsContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Repeat, ArrowRight, X, AlertTriangle, Search } from 'lucide-react';
import { toast } from 'sonner';
import { trNorm } from '@/lib/arama';

const statusLabels = { draft: 'Taslak', completed: 'Tamamlandi', cancelled: 'Iptal' };
const statusColors = {
  completed: 'bg-[#3d6e4e]/10 text-[#3d6e4e]',
  cancelled: 'bg-[#7a3535]/10 text-[#7a3535]'
};

export default function TransferList() {
  const { products } = useProducts();
  const navigate = useNavigate();
  const [transfers, setTransfers] = useState([]);
  const [showNew, setShowNew] = useState(false);

  useEffect(() => {
    api.get('/transfers').then(r => setTransfers(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="transfer-list-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-[#1a1916] tracking-tight">Depolar Arasi Transfer</h1>
          <p className="text-sm text-[#8a7e70] mt-1">{transfers.length} transfer</p>
        </div>
        <Button onClick={() => setShowNew(true)}
          className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] font-semibold" data-testid="new-transfer-btn">
          <Plus size={16} className="mr-2" /> Yeni Transfer
        </Button>
      </div>

      <div className="bg-[#faf7f2] border border-[#d0c4b0] rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Tarih</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Cikis Deposu</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase"></TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Giris Deposu</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Urun Cesidi</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Durum</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Kaydeden</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {transfers.map((t) => (
              <TableRow key={t.id} className="erp-table-row border-[#d0c4b0]">
                {/* İş tarihi gösterilir (kayıt anı değil). Eski kayıtlarda
                    transfer_date yoksa created_at'e düşülür. */}
                <TableCell className="text-sm text-[#3F3E39]">
                  {new Date(t.transfer_date || t.created_at).toLocaleDateString('tr-TR')}
                </TableCell>
                <TableCell className="font-medium text-[#1a1916]">{t.from_warehouse_name}</TableCell>
                <TableCell><ArrowRight size={14} className="text-[#8a7e70]" /></TableCell>
                <TableCell className="font-medium text-[#1a1916]">{t.to_warehouse_name}</TableCell>
                <TableCell className="text-right text-[#3F3E39]">{t.items?.length || 0}</TableCell>
                <TableCell>
                  <span className={`status-badge ${statusColors[t.status] || 'bg-[#e8dfd0] text-[#3F3E39]'}`}>
                    {statusLabels[t.status] || t.status}
                  </span>
                </TableCell>
                <TableCell className="text-sm text-[#8a7e70]">{t.created_by_name}</TableCell>
              </TableRow>
            ))}
            {transfers.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-[#8a7e70]">Transfer bulunamadi</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {showNew && (
        <TransferForm
          onClose={() => setShowNew(false)}
          onComplete={() => { setShowNew(false); api.get('/transfers').then(r => setTransfers(r.data)); }}
        />
      )}
    </div>
  );
}

function TransferForm({ onClose, onComplete }) {
  const [warehouses, setWarehouses] = useState([]);
  const [fromWh, setFromWh] = useState('');
  const [toWh, setToWh] = useState('');
  const [notes, setNotes] = useState('');
  // Transferin IS TARIHI. Varsayilan bugun; gecmis bir gun secilebilir.
  // Stok hareketi bu tarihe yazilir (sunucuda `movement_date`), onay gunune
  // degil — dun yapilan transfer raporda dune dusmeli.
  const [transferDate, setTransferDate] = useState(
    () => new Date().toLocaleDateString('en-CA'));   // yerel gun, YYYY-MM-DD
  const [items, setItems] = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sourceStock, setSourceStock] = useState([]);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  useEffect(() => {
    api.get('/warehouses').then(r => setWarehouses(r.data)).catch(() => {});
  }, []);

  // Load source warehouse stock when fromWh changes
  useEffect(() => {
    if (fromWh) {
      api.get(`/warehouses/${fromWh}/stock`).then(r => setSourceStock(r.data)).catch(() => setSourceStock([]));
    } else {
      setSourceStock([]);
    }
    setItems([]);
  }, [fromWh]);

  // Search products in source stock
  useEffect(() => {
    if (search.length < 2 || !sourceStock.length) { setSearchResults([]); return; }
    // Karşılaştırmanın İKİ tarafı da AYNI normalleştirmeden geçmeli.
    // Burada sorgu `toLowerCase()`, ürün adı `trNorm()` ile normalleniyordu:
    // trNorm "Çilek"i "cilek" yapıyor ama toLowerCase "çilek" bırakıyor, yani
    // "meyve cilek".includes("çilek") hep false dönüyordu. Sonuç: Türkçe harf
    // içeren hiçbir ürün transferde bulunamıyordu (Meyve Çilek, Şeker, Göğüs…).
    const sl = trNorm(search);
    const existing = new Set(items.map(i => i.product_card_id));
    setSearchResults(
      sourceStock.filter(s =>
        !existing.has(s.product_card_id) &&
        (trNorm(s.product_name).includes(sl) || trNorm(s.product_code).includes(sl))
      ).slice(0, 8)
    );
  }, [search, sourceStock, items]);

  const addProduct = (stockItem) => {
    setItems(prev => [...prev, {
      product_card_id: stockItem.product_card_id,
      product_name: stockItem.product_name,
      product_code: stockItem.product_code,
      unit: stockItem.product_unit || stockItem.unit,
      available: stockItem.quantity,
      quantity: ''
    }]);
    setSearch('');
    setSearchResults([]);
  };

  const updateQty = (i, val) => {
    const updated = [...items];
    updated[i] = { ...updated[i], quantity: val };
    setItems(updated);
  };

  const removeItem = (i) => setItems(prev => prev.filter((_, j) => j !== i));

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await api.post('/transfers', {
        from_warehouse_id: fromWh,
        to_warehouse_id: toWh,
        notes,
        transfer_date: transferDate,
        items: items.map(i => ({
          product_card_id: i.product_card_id,
          quantity: parseFloat(i.quantity),
          unit: i.unit
        }))
      });
      toast.success('Transfer tamamlandi');
      setShowConfirm(false);
      onComplete();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata olustu');
    }
    setLoading(false);
  };

  const validItems = items.filter(i => i.quantity && parseFloat(i.quantity) > 0);
  const fromName = warehouses.find(w => w.id === fromWh)?.name || '';
  const toName = warehouses.find(w => w.id === toWh)?.name || '';

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] max-w-3xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-[#1a1916] font-bold flex items-center gap-2">
            <Repeat size={20} className="text-[#2e2d29]" /> Yeni Transfer
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-2">
          {/* Warehouse selection */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Cikis Deposu *</Label>
              <Select value={fromWh} onValueChange={setFromWh}>
                <SelectTrigger className="bg-white border-[#d0c4b0]" data-testid="transfer-from-wh">
                  <SelectValue placeholder="Depo secin" />
                </SelectTrigger>
                <SelectContent>
                  {warehouses.filter(w => w.id !== toWh).map(w => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Giris Deposu *</Label>
              <Select value={toWh} onValueChange={setToWh}>
                <SelectTrigger className="bg-white border-[#d0c4b0]" data-testid="transfer-to-wh">
                  <SelectValue placeholder="Depo secin" />
                </SelectTrigger>
                <SelectContent>
                  {warehouses.filter(w => w.id !== fromWh).map(w => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Product search */}
          {fromWh && toWh && (
            <>
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a7e70]" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder="Urun ara (cikis deposundaki stoktan)..."
                  className="pl-9 bg-white border-[#d0c4b0]" data-testid="transfer-search" />
              </div>
              {searchResults.length > 0 && (
                <div className="border border-[#d0c4b0] rounded-lg bg-white overflow-hidden">
                  {searchResults.map(s => (
                    <button key={s.product_card_id} onClick={() => addProduct(s)}
                      className="w-full flex items-center justify-between p-3 hover:bg-[#e8dfd0]/50 transition-colors border-b border-[#d0c4b0] last:border-b-0 text-left"
                      data-testid={`transfer-add-${s.product_code}`}>
                      <div>
                        <p className="font-semibold text-[#1a1916]">{s.product_name}</p>
                        <p className="text-xs text-[#8a7e70] font-mono">{s.product_code}</p>
                      </div>
                      <p className="text-sm font-semibold text-[#3F3E39]">{s.quantity?.toFixed(1)} {s.product_unit || s.unit}</p>
                    </button>
                  ))}
                </div>
              )}

              {/* Items table */}
              {items.length > 0 && (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
                      <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Urun</TableHead>
                      <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Mevcut</TableHead>
                      <TableHead className="text-[#1a1916] font-bold text-xs uppercase w-28">Miktar</TableHead>
                      <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Birim</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((item, i) => {
                      const qty = parseFloat(item.quantity) || 0;
                      const over = qty > item.available;
                      return (
                        <TableRow key={i} className={`border-[#d0c4b0] ${over ? 'bg-[#7a3535]/5' : ''}`}>
                          <TableCell>
                            <p className="font-medium text-[#1a1916]">{item.product_name}</p>
                            <p className="text-xs text-[#8a7e70] font-mono">{item.product_code}</p>
                          </TableCell>
                          <TableCell className="text-right text-sm text-[#8a7e70]">{item.available?.toFixed(1)}</TableCell>
                          <TableCell>
                            {/* step="any": olculen deger; sabit adim tarayicida "adim uyusmazligi" yapip formu sessizce engelliyor */}
                            <Input type="number" step="any" value={item.quantity}
                              onChange={(e) => updateQty(i, e.target.value)}
                              className={`bg-white border-[#d0c4b0] text-center ${over ? 'border-[#7a3535]' : ''}`}
                              data-testid={`transfer-qty-${i}`} />
                            {over && <p className="text-xs text-[#7a3535] mt-1">Stok yetersiz</p>}
                          </TableCell>
                          <TableCell className="text-sm text-[#8a7e70]">{item.unit}</TableCell>
                          <TableCell>
                            <Button variant="ghost" size="icon" onClick={() => removeItem(i)}
                              className="h-8 w-8 text-[#8a7e70] hover:text-[#7a3535]" data-testid={`transfer-remove-${i}`}>
                              <X size={14} />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </>
          )}

          {/* Transfer tarihi — varsayilan bugun, degistirilebilir */}
          <div className="space-y-2">
            <Label className="text-[#3F3E39] font-medium">Transfer Tarihi</Label>
            <Input type="date" value={transferDate}
              onChange={(e) => setTransferDate(e.target.value)}
              max={new Date().toLocaleDateString('en-CA')}
              className="bg-white border-[#d0c4b0] w-full sm:w-52"
              data-testid="transfer-date" />
            <p className="text-xs text-[#8a7e70]">
              Stok hareketi bu tarihe işlenir. Varsayılan bugündür.
            </p>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label className="text-[#3F3E39] font-medium">Notlar</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Transfer notu..." className="bg-white border-[#d0c4b0] min-h-[60px]" data-testid="transfer-notes" />
          </div>
        </div>

        <DialogFooter className="border-t border-[#d0c4b0] pt-4">
          <Button variant="outline" onClick={onClose} className="border-[#d0c4b0]">Iptal</Button>
          <Button onClick={() => setShowConfirm(true)}
            disabled={!fromWh || !toWh || validItems.length === 0 || items.some(i => parseFloat(i.quantity) > i.available)}
            className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] font-semibold" data-testid="transfer-submit-btn">
            Transferi Tamamla
          </Button>
        </DialogFooter>

        {/* Confirm */}
        <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
          <DialogContent className="bg-[#faf7f2] border-[#d0c4b0]">
            <DialogHeader>
              <DialogTitle className="text-[#1a1916] font-bold flex items-center gap-2">
                <AlertTriangle size={20} className="text-[#7a6a2e]" /> Transfer Onayi
              </DialogTitle>
              <DialogDescription className="text-[#3F3E39] pt-3 text-sm leading-relaxed">
                <span className="font-semibold text-[#1a1916]">{validItems.length} urun</span>{' '}
                <span className="font-semibold text-[#7a3535]">{fromName}</span>'dan{' '}
                <span className="font-semibold text-[#3d6e4e]">{toName}</span>'a transfer edilecek.
                <br /><br />
                Onayliyor musunuz?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowConfirm(false)} className="border-[#d0c4b0]">Iptal</Button>
              <Button onClick={handleSubmit} disabled={loading}
                className="bg-[#3d6e4e] text-white hover:bg-[#2d5e3e] font-semibold" data-testid="transfer-confirm-btn">
                {loading ? 'Isleniyor...' : 'Onayla'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
